// frontend/src/components/fleet/FleetListView.jsx
//
// Vista LISTA operativa de la flota (alternativa al grid de esquemáticos).
//
// Cada unidad ocupa una fila con su silueta, estado, velocidad, distancia y
// tiempo operativo, y debajo una tabla compacta con una COLUMNA POR LLANTA y
// filas de Presión / Temperatura / Estado. Al abrir una unidad se despliega el
// resumen con gráficas, histórico, tickets y plan de acción.
//
// Prioridad de diseño: escanear una flota grande de un vistazo. Por eso la
// barra superior resume el estado y filtra con un clic, las filas son densas y
// solo se renderiza un lote a la vez.
//
// Todo el cálculo viene del backend (`/fleet/overview`): aquí solo se ordena,
// agrupa y formatea.

import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Speed as SpeedIcon,
  Straighten as DistanceIcon,
  AccessTime as TimeIcon,
  ReportProblem as AlertIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import VehicleSilhouette from './VehicleSilhouette';
import FleetVehicleDetail from './FleetVehicleDetail';
import { useUnitPreferences } from '../../hooks/useUnitPreferences';
import {
  formatPressureValue,
  formatTemperatureValue,
  formatSpeedValue,
  formatDistanceValue,
  formatHoursValue,
} from '../../utils/unitFormat';

const PAGE_SIZE = 25;

export const STATUS_META = {
  alarma: { label: 'Alarma', color: 'error', rank: 5 },
  sin_comunicacion: { label: 'Sin comunicación', color: 'default', rank: 4 },
  atencion: { label: 'Requiere ajuste', color: 'warning', rank: 3 },
  parcial: { label: 'Cobertura parcial', color: 'default', rank: 2 },
  sin_sensores: { label: 'Sin sensores', color: 'default', rank: 1 },
  ok: { label: 'OK', color: 'success', rank: 0 },
};

// Orden en que se muestran los contadores de la barra superior.
const STATUS_ORDER = ['alarma', 'atencion', 'sin_comunicacion', 'parcial', 'sin_sensores', 'ok'];

const statusMeta = (status) => STATUS_META[status] || { label: status || '—', color: 'default', rank: 0 };

// Color del estado por posición en la fila "Estado" de la tabla por llanta.
const POSITION_TONE = {
  ok: { color: 'success.main', glyph: '●' },
  subir: { color: 'warning.dark', glyph: '▲' },
  bajar: { color: 'warning.dark', glyph: '▼' },
  sin_reporte: { color: 'text.disabled', glyph: '🛜' },
  sin_sensor: { color: 'text.disabled', glyph: '—' },
};

const positionTone = (position) => POSITION_TONE[position.status] || POSITION_TONE.sin_sensor;

/** Métrica de la cabecera de fila: ícono + valor, sin ocupar dos líneas. */
const RowMetric = ({ icon, label, value }) => (
  <Tooltip title={label}>
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 84 }}>
      {icon}
      <Typography variant="body2" noWrap>{value}</Typography>
    </Stack>
  </Tooltip>
);

RowMetric.propTypes = { icon: PropTypes.node, label: PropTypes.string, value: PropTypes.node };

const VehicleRow = React.memo(({
  vehicle, preferences, expanded, onToggle, selected, onSelect, showDetailMap,
  maxPositions, focusPosition, onFocusPosition,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const meta = statusMeta(vehicle.status);
  const positions = vehicle.positions || [];
  // Se indexa por numero de posicion para que "Pos. 3" caiga siempre en la
  // tercera columna aunque este vehiculo no la tenga.
  const byNumber = new Map(positions.map((p) => [Number(p.position), p]));
  const columns = Array.from({ length: maxPositions }, (_, i) => byNumber.get(i + 1) || null);
  const accent = meta.color === 'default'
    ? theme.palette.grey[400]
    : theme.palette[meta.color].main;

  return (
    <Paper
      variant="outlined"
      onClick={() => onSelect(vehicle.vehicle_id)}
      sx={{
        mb: 0.75,
        borderLeft: '4px solid',
        borderLeftColor: selected ? theme.palette.primary.main : accent,
        bgcolor: selected ? 'action.hover' : 'background.paper',
        cursor: 'pointer',
        transition: 'background-color .15s ease, box-shadow .15s ease',
        '&:hover': { boxShadow: theme.shadows[1] },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.25, py: 0.75, flexWrap: 'wrap' }}>
        <VehicleSilhouette type={vehicle.type} height={26} />

        <Box sx={{ minWidth: 130, flex: '1 1 150px' }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap lineHeight={1.25}>
            {vehicle.vehicle_name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.68rem' }}>
            {[vehicle.type, vehicle.make, vehicle.model].filter(Boolean).join(' · ') || '—'}
          </Typography>
        </Box>

        <Chip
          size="small"
          color={meta.color}
          variant={meta.color === 'default' ? 'outlined' : 'filled'}
          label={meta.label}
          sx={{ height: 21, fontSize: '0.68rem', fontWeight: 700 }}
        />

        <RowMetric
          icon={<SpeedIcon sx={{ fontSize: 16 }} color="action" />}
          label={t('hardcoded.components_fleet.velocidad')}
          value={formatSpeedValue(vehicle.speed_kmh, preferences)}
        />
        <RowMetric
          icon={<DistanceIcon sx={{ fontSize: 16 }} color="action" />}
          label={t('hardcoded.components_fleet.distancia')}
          value={formatDistanceValue(vehicle.distance_km, preferences)}
        />
        <RowMetric
          icon={<TimeIcon sx={{ fontSize: 16 }} color="action" />}
          label={t('hardcoded.components_fleet.tiempo')}
          value={formatHoursValue(vehicle.operating_hours)}
        />

        {!!vehicle.open_tickets && (
          <Tooltip title={t('hardcoded.components_fleet.tickets_activos')}>
            <Chip
              size="small"
              color="error"
              icon={<AlertIcon sx={{ fontSize: 14 }} />}
              label={vehicle.open_tickets}
              sx={{ height: 21, fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }}
            />
          </Tooltip>
        )}

        <Box sx={{ flexGrow: 1 }} />
        <IconButton
          size="small"
          onClick={(event) => { event.stopPropagation(); onToggle(vehicle.vehicle_id); }}
          aria-label={expanded ? 'Cerrar detalle' : 'Abrir detalle'}
          sx={{ p: 0.5 }}
        >
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Rejilla por llanta. El numero de columnas es el MAXIMO de la flota
          visible, no el de este vehiculo: asi la columna "Pos. 7" cae en el
          mismo sitio en todas las filas y la lista se lee como una tabla. Los
          vehiculos con menos posiciones dejan la celda vacia. */}
      {maxPositions > 0 && (
        <Box sx={{ overflowX: 'auto', px: 1.25, pb: 0.75 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `78px repeat(${maxPositions}, minmax(42px, 1fr))`,
              alignItems: 'center',
              rowGap: 0.15,
              minWidth: 78 + maxPositions * 42,
            }}
          >
            {/* Cabecera */}
            <Box sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>Pos.</Box>
            {columns.map((p, index) => (
              <Box
                key={`h-${index}`}
                onClick={(event) => {
                  if (!p) return;
                  event.stopPropagation();
                  onFocusPosition(vehicle.vehicle_id, p.position);
                }}
                sx={{
                  textAlign: 'center', fontSize: '0.62rem', fontWeight: 700,
                  color: focusPosition === p?.position ? 'primary.main' : 'text.disabled',
                  cursor: p ? 'pointer' : 'default',
                  borderRadius: 0.5,
                  bgcolor: focusPosition === p?.position ? 'action.selected' : 'transparent',
                }}
              >
                {p ? p.position : ''}
              </Box>
            ))}

            {/* Presion */}
            <Box sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
              {t('hardcoded.components_fleet.presion')}
            </Box>
            {columns.map((p, index) => (
              <Box key={`p-${index}`} sx={{
                textAlign: 'center', fontSize: '0.75rem', fontWeight: 700,
                bgcolor: 'action.hover', borderRadius: 0.5, py: 0.15,
              }}>
                {p ? formatPressureValue(p.pressure, preferences, { showUnit: false }) : ''}
              </Box>
            ))}

            {/* Temperatura */}
            <Box sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
              {t('hardcoded.components_fleet.temperatura')}
            </Box>
            {columns.map((p, index) => (
              <Box key={`t-${index}`} sx={{
                textAlign: 'center', fontSize: '0.75rem', color: 'text.secondary',
              }}>
                {p ? formatTemperatureValue(p.temperature, preferences, { showUnit: false }) : ''}
              </Box>
            ))}

            {/* Estado */}
            <Box sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
              {t('hardcoded.components_fleet.estado')}
            </Box>
            {columns.map((p, index) => {
              if (!p) return <Box key={`s-${index}`} />;
              const tone = positionTone(p);
              const tip = [p.summary, p.stale && p.age_label ? `Lectura de hace ${p.age_label}` : null]
                .filter(Boolean).join(' · ');
              return (
                <Box key={`s-${index}`} sx={{ textAlign: 'center' }}>
                  <Tooltip title={tip || ''}>
                    <Typography component="span" variant="caption" sx={{
                      fontWeight: 700, fontSize: '0.72rem', color: tone.color,
                      '&::after': p.stale ? {
                        content: '""', display: 'inline-block', width: 4, height: 4,
                        borderRadius: '50%', bgcolor: 'text.disabled', ml: 0.35, mb: '4px',
                      } : undefined,
                    }}>
                      {tone.glyph}
                    </Typography>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 1.25, pb: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
          {expanded && (
            <FleetVehicleDetail
              vehicleId={vehicle.vehicle_id}
              showMap={showDetailMap}
              focusPosition={focusPosition}
              onFocusPosition={(position) => onFocusPosition(vehicle.vehicle_id, position)}
            />
          )}
        </Box>
      </Collapse>
    </Paper>
  );
});

VehicleRow.displayName = 'FleetVehicleRow';
VehicleRow.propTypes = {
  vehicle: PropTypes.object.isRequired,
  preferences: PropTypes.object,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  showDetailMap: PropTypes.bool,
  maxPositions: PropTypes.number,
  focusPosition: PropTypes.number,
  onFocusPosition: PropTypes.func.isRequired,
};

const FleetListView = ({
  vehicles = [],
  selectedVehicleId = null,
  onSelectVehicle = () => {},
  // En la vista dividida el mapa ya vive al lado: el detalle no debe repetirlo.
  showDetailMap = true,
}) => {
  const { t } = useTranslation();
  const { preferences } = useUnitPreferences();
  const [expandedId, setExpandedId] = useState(null);
  const [sortBy, setSortBy] = useState('status');
  const [groupBy, setGroupBy] = useState('none');
  const [statusFilter, setStatusFilter] = useState(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Posicion enfocada POR VEHICULO: al pulsar una columna se filtra su grafica.
  const [focusByVehicle, setFocusByVehicle] = useState({});

  // Todas las filas comparten el mismo numero de columnas: el maximo de la
  // flota visible. Es lo que mantiene las columnas alineadas entre vehiculos.
  const maxPositions = useMemo(
    () => vehicles.reduce((max, v) => Math.max(max, (v.positions || []).length), 0),
    [vehicles],
  );

  const handleFocusPosition = useCallback((vehicleId, position) => {
    // Volver a pulsar la misma posicion quita el filtro.
    setFocusByVehicle((prev) => ({
      ...prev,
      [vehicleId]: prev[vehicleId] === position ? null : position,
    }));
    setExpandedId((current) => (current === vehicleId ? current : vehicleId));
    onSelectVehicle(vehicleId);
  }, [onSelectVehicle]);

  const handleToggle = useCallback((id) => {
    setExpandedId((current) => (current === id ? null : id));
    onSelectVehicle(id);
  }, [onSelectVehicle]);

  // Contadores por estado: resumen y filtro en el mismo control.
  const counts = useMemo(() => {
    const map = {};
    vehicles.forEach((v) => { map[v.status] = (map[v.status] || 0) + 1; });
    return map;
  }, [vehicles]);

  const visible = useMemo(
    () => (statusFilter ? vehicles.filter((v) => v.status === statusFilter) : vehicles),
    [vehicles, statusFilter],
  );

  const sorted = useMemo(() => {
    const compare = {
      status: (a, b) => statusMeta(b.status).rank - statusMeta(a.status).rank
        || (b.open_tickets || 0) - (a.open_tickets || 0)
        || String(a.vehicle_name || '').localeCompare(String(b.vehicle_name || '')),
      name: (a, b) => String(a.vehicle_name || '').localeCompare(String(b.vehicle_name || '')),
      tickets: (a, b) => (b.open_tickets || 0) - (a.open_tickets || 0),
      speed: (a, b) => (b.speed_kmh || 0) - (a.speed_kmh || 0),
      distance: (a, b) => (b.distance_km || 0) - (a.distance_km || 0),
    }[sortBy] || (() => 0);
    return [...visible].sort(compare);
  }, [visible, sortBy]);

  const groups = useMemo(() => {
    if (groupBy === 'none') return [{ key: 'all', label: null, items: sorted }];
    const keyOf = {
      type: (v) => v.type || 'Sin tipo',
      status: (v) => statusMeta(v.status).label,
      alert: (v) => (v.open_tickets ? 'Con alertas' : 'Sin alertas'),
    }[groupBy] || (() => 'all');
    const map = new Map();
    sorted.forEach((v) => {
      const key = keyOf(v);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(v);
    });
    return Array.from(map.entries()).map(([key, items]) => ({ key, label: key, items }));
  }, [sorted, groupBy]);

  if (!vehicles.length) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('hardcoded.components_fleet.sin_vehiculos')}
        </Typography>
      </Paper>
    );
  }

  // Render por lotes: una flota de cientos de unidades no debe pintarse entera.
  let rendered = 0;

  return (
    <Box>
      {/* Barra de estado: resume la flota y filtra con un clic. */}
      <Paper variant="outlined" sx={{ p: 1, mb: 1.25 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
          <Chip
            size="small"
            label={`${vehicles.length} unidades`}
            color={statusFilter ? 'default' : 'primary'}
            variant={statusFilter ? 'outlined' : 'filled'}
            onClick={() => setStatusFilter(null)}
            sx={{ fontWeight: 700 }}
          />
          {STATUS_ORDER.filter((key) => counts[key]).map((key) => {
            const meta = STATUS_META[key];
            const active = statusFilter === key;
            return (
              <Chip
                key={key}
                size="small"
                label={`${meta.label}: ${counts[key]}`}
                color={meta.color}
                variant={active ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter(active ? null : key)}
                sx={{ fontWeight: active ? 700 : 500 }}
              />
            );
          })}

          <Box sx={{ flexGrow: 1 }} />

          <FormControl size="small" sx={{ minWidth: 148 }}>
            <InputLabel id="fleet-sort-label">{t('hardcoded.components_fleet.ordenar_por')}</InputLabel>
            <Select
              labelId="fleet-sort-label"
              value={sortBy}
              label={t('hardcoded.components_fleet.ordenar_por')}
              onChange={(event) => setSortBy(event.target.value)}
            >
              <MenuItem value="status">Estado</MenuItem>
              <MenuItem value="name">Nombre</MenuItem>
              <MenuItem value="tickets">Tickets abiertos</MenuItem>
              <MenuItem value="speed">Velocidad</MenuItem>
              <MenuItem value="distance">Distancia</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 148 }}>
            <InputLabel id="fleet-group-label">{t('hardcoded.components_fleet.agrupar_por')}</InputLabel>
            <Select
              labelId="fleet-group-label"
              value={groupBy}
              label={t('hardcoded.components_fleet.agrupar_por')}
              onChange={(event) => setGroupBy(event.target.value)}
            >
              <MenuItem value="none">Sin agrupar</MenuItem>
              <MenuItem value="type">Tipo de vehículo</MenuItem>
              <MenuItem value="status">Estado</MenuItem>
              <MenuItem value="alert">Alertas</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {sorted.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Ninguna unidad con ese estado.
          </Typography>
          <Button size="small" onClick={() => setStatusFilter(null)}>Quitar filtro</Button>
        </Paper>
      )}

      {groups.map((group) => {
        const remaining = Math.max(0, visibleCount - rendered);
        const items = group.items.slice(0, remaining);
        rendered += items.length;
        if (!items.length) return null;
        return (
          <Box key={group.key} sx={{ mb: 0.5 }}>
            {group.label && (
              <Typography variant="overline" color="text.secondary"
                sx={{ display: 'block', mb: 0.25, lineHeight: 1.6 }}>
                {group.label} ({group.items.length})
              </Typography>
            )}
            {items.map((vehicle) => (
              <VehicleRow
                key={vehicle.vehicle_id}
                vehicle={vehicle}
                preferences={preferences}
                expanded={expandedId === vehicle.vehicle_id}
                onToggle={handleToggle}
                selected={selectedVehicleId === vehicle.vehicle_id}
                onSelect={onSelectVehicle}
                showDetailMap={showDetailMap}
                maxPositions={maxPositions}
                focusPosition={focusByVehicle[vehicle.vehicle_id] || null}
                onFocusPosition={handleFocusPosition}
              />
            ))}
          </Box>
        );
      })}

      {sorted.length > visibleCount && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Button variant="outlined" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
            Mostrar más ({sorted.length - visibleCount})
          </Button>
        </Box>
      )}
    </Box>
  );
};

FleetListView.propTypes = {
  vehicles: PropTypes.array,
  selectedVehicleId: PropTypes.string,
  onSelectVehicle: PropTypes.func,
  showDetailMap: PropTypes.bool,
};

export default FleetListView;
